import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import jsPDF from "jspdf";
import { downloadBlob } from "@/lib/download";
import {
  BookOpen, Rocket, Users2, ListChecks, MapPinned, Map, Zap, Bell, Send,
  UserCircle, Building2, ShieldCheck, Upload, Clock, Sparkles, History, RotateCcw, Copy, BookMarked, Download,
} from "lucide-react";

const hexToRgb = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return [13, 148, 136];
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const urlToDataUrl = (url) => fetch(url).then((r) => r.blob()).then((b) => new Promise((res, rej) => {
  const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(b);
}));

const Section = ({ icon: Icon, title, id, children }) => (
  <Card className="p-6" id={id} data-testid={`manual-section-${id}`}>
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15"><Icon className="h-5 w-5 text-primary" /></div>
      <h2 className="font-heading text-xl font-semibold">{title}</h2>
    </div>
    <div className="mt-4 space-y-2 text-sm leading-relaxed text-muted-foreground">{children}</div>
  </Card>
);

const Li = ({ children }) => (
  <li className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" /><span>{children}</span></li>
);

const B = ({ children }) => <span className="font-medium text-foreground">{children}</span>;

const TOC = [
  ["getting-started", "Getting started", Rocket],
  ["whats-new", "What's new", Sparkles],
  ["roles", "Roles & permissions", ShieldCheck],
  ["leads", "Managing leads", ListChecks],
  ["import", "Importing leads (CSV)", Upload],
  ["sharing", "Sharing & sending leads", Send],
  ["book", "Book of Business", BookMarked],
  ["area", "Area finder", Map],
  ["territory", "Territory zones", MapPinned],
  ["snap", "SNAP & round-robin", Zap],
  ["notifications", "Notifications", Bell],
  ["team", "Team & invitations", Send],
  ["profile", "Profile & branding", UserCircle],
];

export default function UserManual() {
  const { user } = useAuth();
  const [org, setOrg] = useState(null);
  useEffect(() => { api.get("/org").then(({ data }) => setOrg(data)).catch(() => {}); }, []);
  const downloadPDF = async () => {
    const el = document.getElementById("manual-content");
    const text = el ? el.innerText : "";
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const margin = 48;
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const [r, g, b] = hexToRgb(org?.brand_color || "#0D9488");
    doc.setFillColor(r, g, b);
    doc.rect(0, 0, pageW, 70, "F");
    let titleX = margin;
    if (org?.logo) {
      try { const img = await urlToDataUrl(org.logo); doc.addImage(img, margin, 17, 36, 36); titleX = margin + 48; } catch { /* ignore logo */ }
    }
    doc.setTextColor(255, 255, 255); doc.setFontSize(18);
    doc.text(`${org?.name || "IShareLeads"} — User Manual`, titleX, org?.address ? 36 : 44);
    if (org?.address) { doc.setFontSize(10); doc.text(String(org.address), titleX, 52); }
    doc.setTextColor(30, 30, 30); doc.setFontSize(10);
    let y = 98;
    doc.splitTextToSize(text, pageW - margin * 2).forEach((ln) => {
      if (y > pageH - margin) { doc.addPage(); y = margin; }
      doc.text(ln, margin, y); y += 14;
    });
    downloadBlob(doc.output("blob"), "ishareleads-user-manual.pdf", "application/pdf");
  };
  return (
    <div className="max-w-4xl space-y-6" id="manual-content">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-3 font-heading text-3xl font-bold"><BookOpen className="h-7 w-7 text-primary" /> User Manual</h1>
          <p className="mt-1 text-sm text-muted-foreground">Everything you can do in IShareLeads, {user.name.split(" ")[0]}. Jump to a topic or read through.</p>
        </div>
        <Button variant="outline" size="sm" onClick={downloadPDF} data-testid="download-manual-pdf"><Download className="mr-2 h-4 w-4" /> Download PDF</Button>
      </div>

      <Card className="p-5">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {TOC.map(([id, label, Icon]) => (
            <a key={id} href={`#${id}`} data-testid={`manual-toc-${id}`}
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
              <Icon className="h-4 w-4 text-primary" /> {label}
            </a>
          ))}
        </div>
      </Card>

      <Section icon={Sparkles} title="What's new" id="whats-new">
        <ul className="space-y-2">
          <Li><B>Projected commission:</B> add a commission % on any lead to see its projected commission (premium × %) on the form and detail. Your <B>total projected commission</B> across the pipeline shows on the Overview dashboard and in every lead export.</Li>
          <Li><B>Won → Book of Business:</B> mark a lead Won and add it to your Book with one click — or switch on <B>auto-add</B> in Settings so it happens automatically.</Li>
          <Li><B>Faster data entry:</B> the <B>State</B> field is now a US-states dropdown, and <B>City</B> suggests your state's major cities as you type.</Li>
          <Li><B>Download this manual</B> as a PDF anytime with the <B>Download PDF</B> button above.</Li>
          <Li><B>Send leads with a 24-hour timer:</B> hand a lead to a teammate or partner company. A live <B>“returns in Xh Ym”</B> badge counts down (turning red under 3 hours) and, if it isn't opened in 24 hours, the lead automatically returns to you. You're notified when the recipient opens, wins, or loses it.</Li>
          <Li><B>Send History timeline:</B> every lead now shows a full trail of who it was sent to, when it was opened, and how it ended (won / lost / returned). Click a send-related notification to jump straight to that lead's Send History.</Li>
          <Li><B>Bulk actions:</B> tick several leads to <B>Send</B> them to one recipient at once, add them to the master pool, export, or <B>Delete</B> them together (with a confirmation that lists exactly which leads).</Li>
          <Li><B>Undo anything:</B> deletes, status changes, and reassignments all show an <B>Undo</B> toast — and the <B>Recent</B> panel keeps a running list so you can undo even after the toast disappears.</Li>
          <Li><B>Smarter CSV import:</B> a live <B>preview table</B> shows parsed rows before importing, you can <B>map your own column names</B> to fields (and it remembers your mapping next time), and possible <B>duplicates</B> (matching an existing phone or email) are flagged so you can skip them.</Li>
          <Li><B>Book of Business:</B> keep every active policy in one place with carriers, renewal dates, premium analytics, CSV/PDF export, and one-click <B>Policy → Lead</B> conversion.</Li>
        </ul>
      </Section>

      <Section icon={Rocket} title="Getting started" id="getting-started">
        <ul className="space-y-2">
          <Li><B>Sign in</B> with your email &amp; password, or with Google. New agencies can self-register — the creator becomes the agency <B>Admin</B>.</Li>
          <Li>Your agency is a private workspace (multi-tenant): you only ever see your own agency's leads and team.</Li>
          <Li>The left sidebar navigates: <B>Overview</B>, <B>Leads</B>, <B>Book of Business</B>, <B>Area Finder</B>, <B>Territory</B>, <B>Directory</B>, <B>Team</B>, <B>Scripts</B>, <B>Billing</B>, <B>User Manual</B>, and <B>Settings</B>.</Li>
          <Li>Toggle <B>dark/light mode</B> with the sun/moon button in the top bar.</Li>
        </ul>
      </Section>

      <Section icon={ShieldCheck} title="Roles & permissions" id="roles">
        <ul className="space-y-2">
          <Li><B>Admin</B> — full control: manage team, invite members, rename the agency, upload the company logo, and everything Managers can do.</Li>
          <Li><B>Manager</B> — sees all agency leads; can assign, SNAP, share, edit and delete leads.</Li>
          <Li><B>Agent</B> — works leads assigned to them, shared with them, created by them, or still unassigned; can claim and edit those leads and manage their own territory zones.</Li>
        </ul>
      </Section>

      <Section icon={ListChecks} title="Managing leads" id="leads">
        <ul className="space-y-2">
          <Li><B>Add Lead</B> — click <B>Add Lead</B> on the Leads page. Start typing the address for Google autocomplete (auto-fills city/state/ZIP and map location). Phone auto-formats to (###) ###-####. Add a <B>Company name</B>, insurance type, status, premium and notes.</Li>
          <Li><B>Insurance types</B> — Life, Health, Casualty, Final Needs, Group Insurance, and Business Loans.</Li>
          <Li><B>Row actions</B> — each lead row has <B>View</B> (details, map image, notes &amp; activity log), <B>Call</B>, <B>Print</B>, <B>Edit</B>, and for Admin/Manager a <B>SNAP</B> menu and <B>Delete</B>.</Li>
          <Li><B>Notes</B> — open a lead and add running notes; <B>@mention</B> a teammate to notify them, and every note is timestamped.</Li>
          <Li><B>Bulk actions</B> — tick the checkboxes to select leads, then <B>Send</B> them to one recipient, <B>Add to master pool</B>, <B>Export</B>, or <B>Delete</B> them together.</Li>
          <Li><B>Undo &amp; Recent changes</B> — after a delete, status change, or reassignment an <B>Undo</B> button appears in the toast. The <B>Recent</B> button (top of the Leads page) keeps a list of recent changes so you can undo them later too.</Li>
          <Li><B>Filter &amp; search</B> by product, status, lead age, or free text (name/email/phone), and sort newest/oldest.</Li>
          <Li>Every change is recorded in the lead's <B>activity log</B> (created, edited, status change, assigned, claimed, shared, snapped, sent, opened).</Li>
        </ul>
      </Section>

      <Section icon={Upload} title="Importing leads (CSV)" id="import">
        <ul className="space-y-2">
          <Li><B>Download the template</B> for the standard columns: <span className="font-mono text-xs">name, company, email, phone, address, city, state, zip, insurance_type, status, premium_estimate, notes</span>. Fields containing commas (like an address) should be wrapped in quotes — the importer handles that automatically.</Li>
          <Li><B>Column mapping</B> — your file doesn't have to match the template. After you upload, match each field to one of your columns; common names (e.g. “Full Name”, “Street Address”, “Postal Code”) are detected for you. <History className="inline h-3.5 w-3.5" /> Your mapping is <B>remembered</B> for that file layout next time.</Li>
          <Li><B>Preview before importing</B> — a scrollable table shows each parsed lead's name, address and city/state. Rows without a name are skipped and counted.</Li>
          <Li><B>Duplicate detection</B> <Copy className="inline h-3.5 w-3.5" /> — rows whose phone or email already exists in your agency are flagged, and you can tick <B>Skip duplicates</B> so they aren't imported twice.</Li>
        </ul>
      </Section>

      <Section icon={Send} title="Sharing & sending leads" id="sharing">
        <ul className="space-y-2">
          <Li><B>Send a lead (24h timer)</B> — from a lead's detail, send it to a <B>teammate</B> or a partner <B>company</B>. The recipient must open it within 24 hours or it automatically returns to you.</Li>
          <Li><B>Return timer badge</B> — sent, unopened leads show a live <B>“returns in Xh Ym”</B> countdown on the lead, in the list, and in the detail header; it turns red in the final 3 hours.</Li>
          <Li><B>You're kept in the loop</B> — you're notified when the recipient <B>opens</B>, <B>wins</B>, or <B>loses</B> the lead. Marking a received lead as lost requires a short reason.</Li>
          <Li><B>Send History</B> <History className="inline h-3.5 w-3.5" /> — each lead keeps a timeline of every send: who it went to, when it was opened, and the outcome. Click a send-related notification to jump straight to it.</Li>
          <Li><B>Partner agencies &amp; master pool</B> — share leads with accepted partner agencies, and (for master accounts) into a shared <B>master pool</B> visible to all member companies.</Li>
        </ul>
      </Section>

      <Section icon={BookMarked} title="Book of Business" id="book">
        <ul className="space-y-2">
          <Li><B>Track active policies</B> — record insured name, carrier (managed dropdown), policy number, address, DOB, phone, premium, renewal date, and additional covered people.</Li>
          <Li><B>Renewals</B> — see policies renewing in 30/60/90 days, get a “Renewals due” sidebar badge, and a weekly renewals digest email.</Li>
          <Li><B>Analytics &amp; export</B> — carrier breakdown and a 12-month premium trend chart; export the whole book to <B>CSV or PDF</B>, and bulk-import with column mapping.</Li>
          <Li><B>Policy → Lead</B> — turn any policy into a pipeline lead in one click for cross-sell or renewal outreach.</Li>
        </ul>
      </Section>

      <Section icon={Map} title="Area finder" id="area">
        <ul className="space-y-2">
          <Li>Enter a <B>ZIP code</B> and drag the <B>radius</B> slider (5–50 miles) to find leads near that point; filter by product.</Li>
          <Li>Matching leads appear as color-coded pins on the map and in the results list with distances. Agents can <B>claim</B> unassigned leads right there.</Li>
          <Li>If the ZIP is a claimed <B>territory</B>, a banner suggests the owning agent.</Li>
        </ul>
      </Section>

      <Section icon={MapPinned} title="Territory zones" id="territory">
        <ul className="space-y-2">
          <Li>On the <B>Territory</B> page, agents claim the ZIP codes they cover. Admins/Managers can assign a ZIP to any teammate.</Li>
          <Li>Each ZIP belongs to one owner. When someone searches that ZIP in the Area Finder, IShareLeads suggests the right owner.</Li>
          <Li>Release a zone with the trash icon (owner, or Admin/Manager).</Li>
        </ul>
      </Section>

      <Section icon={Zap} title="SNAP & round-robin" id="snap">
        <ul className="space-y-2">
          <Li><B>SNAP</B> (Admin/Manager) exclusively assigns a lead to one agent, clears any sharing, timestamps it, and instantly notifies that agent.</Li>
          <Li><B>Next agent (round-robin)</B> — the first option in the SNAP menu auto-assigns to the next agent in rotation, one tap.</Li>
          <Li><B>SLA button</B> — while a snapped lead is unattended, a badge shows urgency: <span className="font-medium text-emerald-500">green flashing ≤24h</span>, <span className="font-medium text-amber-500">yellow flashing 25–72h</span>, <span className="font-medium text-red-500">red blinking &gt;72h</span>. It clears once the assigned agent attends the lead (changes status or claims).</Li>
        </ul>
      </Section>

      <Section icon={Bell} title="Notifications" id="notifications">
        <ul className="space-y-2">
          <Li>The <B>bell</B> in the top bar shows unread alerts — a lead snapped or assigned to you, a sent lead opened/won/lost/returned, and @mentions. Opening the panel marks them read.</Li>
          <Li><B>Click a notification</B> to jump to that lead. Send-related alerts open straight to the lead's <B>Send History</B>.</Li>
        </ul>
      </Section>

      <Section icon={Send} title="Team & invitations" id="team">
        <ul className="space-y-2">
          <Li><B>Invite teammate</B> (Admin) emails an invitation link; the teammate sets their <B>own password</B> and joins your agency. Pending invites are listed and can be revoked.</Li>
          <Li><B>Add directly</B> (Admin) creates a member with a temporary password you set.</Li>
          <Li>Change any member's <B>role</B> or toggle them <B>active/inactive</B> from the team table.</Li>
        </ul>
      </Section>

      <Section icon={UserCircle} title="Profile & branding" id="profile">
        <ul className="space-y-2">
          <Li><B>Profile picture</B> — every user can upload their own photo in <B>Settings</B> (shown in the sidebar).</Li>
          <Li><B>Company logo</B> (Admin) — upload in Settings; it replaces the shield in the sidebar header. <Building2 className="inline h-3.5 w-3.5" /></Li>
          <Li><B>Agency name</B> — Admins can rename the agency in Settings. <Upload className="inline h-3.5 w-3.5" /> Images must be PNG/JPG/GIF/WEBP under 5MB.</Li>
        </ul>
      </Section>

      <p className="flex items-center gap-2 pb-4 text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5" /> Manual reflects the current feature set. Have an idea? Ask your admin to request it.</p>
    </div>
  );
}
